<template>
    <div class="product-page">
        <page-header :title="title" :items="items" />

        <b-alert
            v-if="product.id && !product.variantsCount"
            show
            variant="info"
        >
            This product has no variants. It won't be visible in APP. Please add
            at least one variant.
        </b-alert>

        <tabs
            :items="tabsElements"
            :active-item="activeTab"
            is-card
            class="mb-3"
            @item-change="activeTab = $event"
        />

        <div v-if="!editMode || (editMode && product.id)" class="row">
            <div class="col-xl-9 mb-3">
                <div class="card">
                    <div class="card-body">
                        <add-edit-form
                            v-if="activeTab.value === 'main'"
                            v-model="product"
                            :edit-mode="editMode"
                            :validation="$v"
                            :server-errors="serverErrors"
                            @on-name-change="onNameChange"
                        />
                        <variants-table v-else :product-id="product.id" />
                    </div>
                </div>
            </div>

            <div class="col-xl-3 mb-3">
                <div class="card">
                    <div class="card-body">
                        <div :class="{ 'mb-3': editMode }">
                            <a
                                v-if="editMode"
                                :href="previewUrl"
                                class="btn btn-primary btn-static waves-effect waves-light m-1"
                                target="_blank"
                                @click="onPreviewButtonClick"
                            >
                                <i class="far fa-eye" />
                                Preview
                            </a>

                            <button
                                class="btn btn-success btn-static waves-effect waves-light m-1"
                                @click="submitHandler"
                            >
                                <i class="fas fa-save" />
                                Save
                            </button>
                        </div>

                        <item-information
                            :key="informationKey"
                            :edit-mode="editMode"
                            :sections="sections"
                            :item="product"
                            :slug="product.slug"
                            :options="options"
                            :slug-error="slugError"
                            :featured-image="product.featuredImageId"
                            get-tags-function-name="productTags/index"
                            :tags="product.tags"
                            @meta-change="onMetaChange"
                            @on-slug-change="onSlugChange"
                            @on-delete-confirm="onDeleteConfirm"
                            @on-featured-image-change="onFeaturedImageChange"
                            @on-tags-change="onTagsChange"
                        />
                    </div>
                </div>
            </div>
        </div>
        <skeleton-loader v-else />
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import { StatusCodes as HTTP } from 'http-status-codes';
import { required } from 'vuelidate/lib/validators';
import { frontendUrl } from '@/config';
import Tabs from '@/components/elements/Tabs';
import AddEditForm from '@/components/products/AddEditForm';
import VariantsTable from '@/components/products/VariantsTable';
import ItemInformation from '@/components/elements/ItemInformation';

export default {
    page: {
        title: 'Manage Product'
    },

    validations: {
        product: {
            name: {
                required
            },
            slug: {
                required
            },
            description: {
                required
            },
            price: {
                required
            },
            agudaPrice: {},
            supplierId: {
                required
            }
        }
    },

    components: {
        Tabs,
        AddEditForm,
        VariantsTable,
        ItemInformation
    },

    data() {
        const { id = null, shopId } = this.$route.params;

        return {
            id,
            editMode: !!id,
            product: {
                price: 0,
                agudaPrice: null,
                meta: {},
                tags: [],
                slug: ''
            },
            shop: {},
            informationKey: 0,
            serverErrors: [],
            productValueStringify: '',
            shopId,
            activeTab: {
                value: 'main'
            }
        };
    },

    computed: {
        sections() {
            const sections = {
                permalink: true,
                meta: true,
                featuredImage: true,
                tags: true
            };

            if (this.editMode) {
                sections.status = true;
            }

            return sections;
        },

        previewUrl() {
            return `${frontendUrl}/products/${this.product.slug}`;
        },

        slugError() {
            return this.$getValidationErrorMessageHandler.handle(
                this.$v.product,
                this.serverErrors,
                'slug'
            );
        },

        title() {
            return `${this.editMode ? 'Edit' : 'Add'} ${
                this.shop.name
            }'s Product`;
        },

        items() {
            return [
                {
                    text: 'Aguda',
                    to: '/'
                },
                {
                    text: 'Shops',
                    to: '/shops'
                },
                {
                    text: this.shop.name,
                    to: `/shops/${this.shop.id}/edit`
                },
                {
                    text: `${this.shop.name}'s Products`,
                    to: `/shops/${this.shop.id}/products`
                },
                {
                    text: `${this.editMode ? 'Edit' : 'Add'} Product`,
                    active: true
                }
            ];
        },

        options() {
            return { slugBasicUrl: 'products' };
        },

        tabsElements() {
            const elements = [
                {
                    text: 'Main',
                    value: 'main'
                }
            ];

            if (!this.editMode) {
                return elements;
            }

            return [
                ...elements,
                {
                    text: 'Variants',
                    value: 'variants'
                }
            ];
        }
    },

    async created() {
        try {
            this.shop = await this.getShop(this.shopId);
        } catch (error) {
            console.error(error);

            this.$toasterError();
        }

        if (this.editMode) {
            try {
                this.product = await this.getProduct(this.id);

                this.productValueStringify = JSON.stringify(this.product);
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        }
    },

    methods: {
        ...mapActions({
            getShop: 'shops/show',
            addProduct: 'products/store',
            deleteProduct: 'products/destroy',
            getProduct: 'products/show',
            updateProduct: 'products/update'
        }),

        async submitHandler() {
            this.serverErrors = [];

            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            const tags = this.product.tags.map(tag => tag.id);

            const data = {
                ...this.product,
                onlyForAgudaMembers: !!this.product.onlyForAgudaMembers,
                tags
            };

            if (data.onlyForAgudaMembers) {
                data.agudaPrice = null;
            }

            try {
                if (!this.editMode) {
                    data.shopId = this.shopId;

                    const added = await this.addProduct(data);

                    this.$router.push(
                        `/shops/${this.shopId}/products/${added.id}/edit`
                    );
                } else {
                    const { updatedAt } = await this.updateProduct({
                        id: this.id,
                        data
                    });

                    this.product.updatedAt = updatedAt;
                    this.productValueStringify = JSON.stringify(this.product);

                    this.$toaster('Product updated!');
                }
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.BAD_REQUEST && data.errors) {
                        this.serverErrors = data.errors;

                        this.$toasterError('Recheck your form.');

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        },

        async onDeleteConfirm() {
            try {
                await this.deleteProduct(this.id);

                this.$router.push(`/shops/${this.shop.id}/products`);
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        },

        onPreviewButtonClick(e) {
            if (this.productValueStringify !== JSON.stringify(this.product)) {
                e.preventDefault();

                this.$toaster('Please save changes before preview!', {
                    title: 'Warning!',
                    variant: 'warning'
                });
            }
        },

        onMetaChange(meta) {
            this.product.meta = { ...meta };
        },

        onNameChange() {
            if (this.editMode) {
                return;
            }

            this.onSlugChange(this.product.name);
            this.informationKey++;
        },

        onSlugChange(slug) {
            this.product.slug = slug;
        },

        onFeaturedImageChange(imageId) {
            this.product.featuredImageId = imageId;
        },

        onTagsChange(tags) {
            this.product.tags = tags;
            this.informationKey++;
        }
    }
};
</script>
